import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowUpRight, FiGithub, FiLinkedin, FiMail } from "react-icons/fi";
import chatify from "../../Assets/Projects/chatify.png";
import bitsOfCode from "../../Assets/Projects/blog.png";
import editor from "../../Assets/Projects/codeEditor.png";
import leaf from "../../Assets/Projects/leaf.png";

const projects = [
  { number: "01", title: "Hextant Labs", type: "AI tools / Product engineering", description: "A focused AI studio for turning ambitious ideas into useful tools.", image: chatify, tone: "peach", link: "https://hexy.hextantlabs.com" },
  { number: "02", title: "KDU Blog", type: "Publishing platform / Full-stack", description: "A calm, capable publishing system with a custom admin experience.", image: bitsOfCode, tone: "blue", link: "https://kdublog.vercel.app" },
  { number: "03", title: "NACOS Portal", type: "Student tools / React", description: "One useful place for dues, past questions, and academic planning.", image: editor, tone: "yellow", link: "https://nacoskdu.vercel.app" },
  { number: "04", title: "CurEase Healthcare", type: "Health tech / Product design", description: "Making patient and doctor communication feel a little more human.", image: leaf, tone: "green", link: "https://curease.vercel.app" },
];

function Reveal({ children, className = "", delay = 0 }) {
  const revealRef = useRef(null);

  useEffect(() => {
    const element = revealRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        element.style.setProperty("--reveal-delay", `${delay}ms`);
        element.classList.add("is-visible");
        observer.unobserve(element);
      }
    }, { threshold: 0.16 });

    observer.observe(element);
    return () => observer.disconnect();
  }, [delay]);

  return <div ref={revealRef} className={`reveal ${className}`}>{children}</div>;
}

function SlidingProject({ project, index }) {
  const slideRef = useRef(null);

  useEffect(() => {
    const element = slideRef.current;
    if (!element) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return undefined;

    let frame;
    const updateSlide = () => {
      frame = window.requestAnimationFrame(() => {
        const bounds = element.getBoundingClientRect();
        const viewport = window.innerHeight;
        const progress = Math.min(1, Math.max(0, (viewport - bounds.top) / (viewport + bounds.height)));
        element.style.setProperty("--slide-progress", progress.toFixed(3));
      });
    };

    updateSlide();
    window.addEventListener("scroll", updateSlide, { passive: true });
    window.addEventListener("resize", updateSlide);
    return () => {
      window.removeEventListener("scroll", updateSlide);
      window.removeEventListener("resize", updateSlide);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <Reveal delay={index * 110}>
      <div ref={slideRef} className="case-study-slide">
        <a className={`case-study ${project.tone}`} href={project.link} target="_blank" rel="noreferrer">
          <div className="case-study-copy"><span className="project-number">{project.number}</span><div><p className="project-type">{project.type}</p><h3>{project.title}<FiArrowUpRight /></h3><p className="project-description">{project.description}</p></div></div>
          <div className="project-image-wrap"><img src={project.image} alt={`${project.title} project preview`} /></div>
        </a>
      </div>
    </Reveal>
  );
}

function Home() {
  return (
    <main className="portfolio-home">
      <section className="intro-section" id="home">
        <div className="intro-stagger"><div className="eyebrow">Frontend developer · Product builder</div>
          <h1>Hi, I&apos;m <span>Olonade<br />Oluwanifemi.</span></h1>
          <div className="intro-bottom"><p>I design and build thoughtful digital products for people, teams, and ideas that deserve to move forward.</p><a className="circle-link" href="#work" aria-label="Jump to selected work"><FiArrowUpRight /></a></div>
        </div>
      </section>

      <Reveal><section className="statement-section"><div className="section-label">A little about me</div><div><h2>I like making the complicated feel <em>clear.</em></h2><p>I&apos;m a BSc Computer Science graduate and frontend developer. My work sits between design and engineering: clean interfaces, useful systems, and the small details that make software feel natural.</p></div></section></Reveal>

      <section className="work-section" id="work"><Reveal><div className="work-heading"><div className="section-label">Selected work</div><p>Some things I&apos;ve helped bring to life.</p></div></Reveal><div className="case-study-list">
        {projects.map((project, index) => <SlidingProject key={project.title} project={project} index={index} />)}
      </div><Reveal><Link to="/project" className="text-link">See all projects <FiArrowUpRight /></Link></Reveal></section>

      <Reveal><section className="approach-section"><div className="section-label">How I work</div><div className="approach-grid"><div><span>01</span><h3>Listen first</h3><p>Good work starts with understanding the people and the problem behind the brief.</p></div><div><span>02</span><h3>Make it useful</h3><p>Every screen should earn its place, feel considered, and help someone get somewhere.</p></div><div><span>03</span><h3>Ship with care</h3><p>I bring the polish of design and the pragmatism of code into the same room.</p></div></div></section></Reveal>

      <Reveal><section className="contact-section" id="contact"><div className="section-label">Have a good one?</div><h2>Let&apos;s make<br /><em>something meaningful.</em></h2><a className="contact-email" href="mailto:olonadenifemi@gmail.com">olonadenifemi@gmail.com <FiArrowUpRight /></a><div className="social-row"><a href="https://github.com/ProgrammerDavid1234" target="_blank" rel="noreferrer"><FiGithub /> GitHub</a><a href="https://www.linkedin.com/in/olonadenifemi/" target="_blank" rel="noreferrer"><FiLinkedin /> LinkedIn</a><a href="mailto:olonadenifemi@gmail.com"><FiMail /> Email</a></div></section></Reveal>
    </main>
  );
}

export default Home;
